import { formatDate } from 'util/formatDate';

import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { AttendeesListIcon, DeleteIcon } from 'components/icons';
import React, { ReactElement, useState } from 'react';
import { Event } from 'services/api';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import AddIcon from '@mui/icons-material/Add';

import { Modal } from '../Modal';

type OccurrenceFilter = 'upcoming' | 'past';
type ModalMode = 'view-only' | 'admin';

export interface ViewAllDatesModalProps {
  open: boolean;
  onClose: () => void;
  eventTitle: string;
  occurrences: Event[];
  isLoading?: boolean;
  onDeleteOccurrence?: (occurrenceId: string) => void;
  onViewAttendees?: (occurrenceId: string) => void;
  onCheckInAttendees?: (occurrenceId: string) => void;
  filter: OccurrenceFilter;
  onFilterChange: (filter: OccurrenceFilter) => void;
  counts?: {
    upcoming: number;
    past: number;
  };
  mode?: ModalMode;
}

interface FilterTabsProps {
  filter: OccurrenceFilter;
  onFilterChange: (filter: OccurrenceFilter) => void;
  counts?: { upcoming: number; past: number };
  isMobile: boolean;
}

const FilterTabs = ({ filter, onFilterChange, counts, isMobile }: FilterTabsProps) => (
  <Box
    sx={{
      px: isMobile ? 16 : 32,
      display: 'flex',
      gap: isMobile ? 16 : 32,
      borderBottom: '1px solid #E5E5E5',
    }}
  >
    <Box
      onClick={() => onFilterChange('upcoming')}
      sx={{
        pb: 12,
        cursor: 'pointer',
        borderBottom: filter === 'upcoming' ? '2px solid #1A1A1A' : '2px solid transparent',
        marginBottom: '-1px',
      }}
    >
      <Typography
        sx={{
          fontSize: 14,
          fontWeight: filter === 'upcoming' ? 600 : 400,
          color: filter === 'upcoming' ? '#1A1A1A' : '#999999',
        }}
      >
        Upcoming ({counts?.upcoming ?? 0})
      </Typography>
    </Box>
    <Box
      onClick={() => onFilterChange('past')}
      sx={{
        pb: 12,
        cursor: 'pointer',
        borderBottom: filter === 'past' ? '2px solid #1A1A1A' : '2px solid transparent',
        marginBottom: '-1px',
      }}
    >
      <Typography
        sx={{
          fontSize: 14,
          fontWeight: filter === 'past' ? 600 : 400,
          color: filter === 'past' ? '#1A1A1A' : '#999999',
        }}
      >
        Past ({counts?.past ?? 0})
      </Typography>
    </Box>
  </Box>
);

interface OccurrenceListProps {
  occurrences: Event[];
  isLoading: boolean;
  filter: OccurrenceFilter;
  isAdminMode: boolean;
  isMobile: boolean;
  onOccurrenceClick: (occurrence: Event) => void;
}

const OccurrenceList = ({
  occurrences,
  isLoading,
  filter,
  isAdminMode,
  isMobile,
  onOccurrenceClick,
}: OccurrenceListProps) => {
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={32}>
        <Typography color="text.secondary">Loading...</Typography>
      </Box>
    );
  }

  if (occurrences.length === 0) {
    return (
      <Box display="flex" justifyContent="center" py={32}>
        <Typography color="text.secondary">No {filter} occurrences found</Typography>
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" gap={0}>
      {occurrences.map(occurrence => (
        <Box
          key={occurrence.id}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 16,
            px: 16,
            cursor: isAdminMode ? 'pointer' : 'default',
            borderRadius: '8px',
            '&:hover': isAdminMode ? { backgroundColor: '#F8F8F8' } : {},
          }}
          onClick={() => isAdminMode && onOccurrenceClick(occurrence)}
        >
          <Typography
            sx={{
              fontSize: isMobile ? 14 : 16,
              fontWeight: 400,
              color: '#1A1A1A',
              flex: 1,
            }}
          >
            {formatDate(new Date(occurrence.date), {
              includeDay: true,
              includeDate: true,
              includeYear: true,
            })}
          </Typography>
          {isAdminMode && <ChevronRightIcon sx={{ fontSize: 20, color: '#999999' }} />}
        </Box>
      ))}
    </Box>
  );
};

interface ActionsPanelProps {
  selectedOccurrence: Event;
  onCheckInAttendees?: (id: string) => void;
  onViewAttendees?: (id: string) => void;
  onDeleteOccurrence?: (id: string) => void;
  onBack: () => void;
  onClose: () => void;
}

const ActionsPanel = ({
  selectedOccurrence,
  onCheckInAttendees,
  onViewAttendees,
  onDeleteOccurrence,
  onBack,
  onClose,
}: ActionsPanelProps) => {
  const isPastDate = new Date(selectedOccurrence.date).valueOf() < new Date().valueOf();

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        p: '24px 32px',
      }}
    >
      <Box
        onClick={onBack}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          mb: 24,
          cursor: 'pointer',
          width: 'fit-content',
        }}
      >
        <ChevronLeftIcon sx={{ fontSize: 20, color: '#1A1A1A' }} />
        <Typography
          sx={{
            fontSize: 16,
            fontWeight: 500,
            color: '#1A1A1A',
          }}
        >
          Back
        </Typography>
      </Box>

      <Typography
        sx={{
          fontSize: 24,
          fontWeight: 600,
          color: '#1A1A1A',
          mb: 32,
        }}
      >
        {formatDate(new Date(selectedOccurrence.date), {
          includeDay: true,
          includeDate: true,
          includeYear: true,
        })}
      </Typography>

      <Box display="flex" flexDirection="column" gap={8}>
        {onCheckInAttendees && (
          <Box
            onClick={() => onCheckInAttendees(selectedOccurrence.id)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              py: 12,
              px: 0,
              cursor: 'pointer',
              borderRadius: '8px',
              '&:hover': { backgroundColor: '#F8F8F8' },
            }}
          >
            <Box display="flex" alignItems="center" gap={12}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '8px',
                  backgroundColor: '#FFE733',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <AddIcon sx={{ fontSize: 16, color: '#000000' }} />
              </Box>
              <Typography sx={{ fontSize: 16, fontWeight: 400, color: '#1A1A1A' }}>
                Check In Attendees
              </Typography>
            </Box>
            <ChevronRightIcon sx={{ fontSize: 20, color: '#999999' }} />
          </Box>
        )}

        {onViewAttendees && (
          <Box
            onClick={() => onViewAttendees(selectedOccurrence.id)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              py: 12,
              px: 0,
              cursor: 'pointer',
              borderRadius: '8px',
              '&:hover': { backgroundColor: '#F8F8F8' },
            }}
          >
            <Box display="flex" alignItems="center" gap={12}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '8px',
                  backgroundColor: '#F5F5F5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <AttendeesListIcon sx={{ fontSize: 18, color: '#1A1A1A' }} />
              </Box>
              <Typography sx={{ fontSize: 16, fontWeight: 400, color: '#1A1A1A' }}>
                View Attendee List
              </Typography>
            </Box>
            <ChevronRightIcon sx={{ fontSize: 20, color: '#999999' }} />
          </Box>
        )}

        {onDeleteOccurrence && !isPastDate && (
          <Box
            onClick={() => onDeleteOccurrence(selectedOccurrence.id)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              py: 12,
              px: 0,
              cursor: 'pointer',
              borderRadius: '8px',
              '&:hover': { backgroundColor: '#F8F8F8' },
            }}
          >
            <Box display="flex" alignItems="center" gap={12}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '8px',
                  backgroundColor: '#FFEBEE',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <DeleteIcon sx={{ fontSize: 18, color: '#D32F2F' }} />
              </Box>
              <Typography sx={{ fontSize: 16, fontWeight: 400 }}>
                Cancel This Date
              </Typography>
            </Box>
            <ChevronRightIcon sx={{ fontSize: 20, color: '#999999' }} />
          </Box>
        )}
      </Box>

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'center',
          pt: 32,
        }}
      >
        <Typography
          onClick={onClose}
          sx={{
            fontSize: 16,
            fontWeight: 500,
            color: '#1A1A1A',
            textDecoration: 'underline',
            cursor: 'pointer',
          }}
        >
          Close
        </Typography>
      </Box>
    </Box>
  );
};

export const ViewAllDatesModal = ({
  open,
  onClose,
  eventTitle,
  occurrences,
  isLoading = false,
  onDeleteOccurrence,
  onViewAttendees,
  onCheckInAttendees,
  filter,
  onFilterChange,
  counts,
  mode = 'view-only',
}: ViewAllDatesModalProps): ReactElement => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedOccurrence, setSelectedOccurrence] = useState<Event | null>(null);

  const isAdminMode = mode === 'admin';
  const showActionsView = isAdminMode && selectedOccurrence;

  const handleDeleteOccurrence = (id: string) => {
    onDeleteOccurrence?.(id);
    setSelectedOccurrence(null);
  };

  const handleBack = () => {
    setSelectedOccurrence(null);
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          width: isMobile ? '100%' : 500,
          maxWidth: isMobile ? '100%' : 500,
          maxHeight: '80vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: isMobile ? '16px 16px 0 0' : 16,
          backgroundColor: '#fff',
        }}
      >
        {!showActionsView && (
          <>
            <Box
              sx={{
                p: isMobile ? '20px 16px' : '24px 32px',
                borderBottom: 'none',
              }}
            >
              <Typography
                sx={{
                  fontSize: isMobile ? 20 : 24,
                  fontWeight: 600,
                  color: '#1A1A1A',
                  mb: 4,
                }}
              >
                {eventTitle}
              </Typography>
              <Typography
                sx={{
                  fontSize: 14,
                  color: '#666666',
                }}
              >
                {isAdminMode ? 'Select a date to manage' : 'View all scheduled dates for this event.'}
              </Typography>
            </Box>

            <FilterTabs
              filter={filter}
              onFilterChange={onFilterChange}
              counts={counts}
              isMobile={isMobile}
            />

            <Box
              sx={{
                flex: 1,
                overflow: 'auto',
                px: isMobile ? 16 : 32,
                py: 16,
              }}
            >
              <OccurrenceList
                occurrences={occurrences}
                isLoading={isLoading}
                filter={filter}
                isAdminMode={isAdminMode}
                isMobile={isMobile}
                onOccurrenceClick={setSelectedOccurrence}
              />
            </Box>
          </>
        )}

        {showActionsView && selectedOccurrence && (
          <ActionsPanel
            selectedOccurrence={selectedOccurrence}
            onCheckInAttendees={onCheckInAttendees}
            onViewAttendees={onViewAttendees}
            onDeleteOccurrence={handleDeleteOccurrence}
            onBack={handleBack}
            onClose={onClose}
          />
        )}
      </Box>
    </Modal>
  );
};
